import { clearCardLoading, setCardLoading, useCardLoading } from "@components/HomePage/CardParts/CardStore";
import { getCardState } from "@components/HomePage/CardParts/getCardState";
import useRemoteProgress from "@ext/git/actions/Clone/logic/useRemoteProgress";
import { type CatalogLink, isCloningStatus } from "@ext/navigation/NavigationLinks";
import { useCallback, useEffect, useState } from "react";

const useCardState = (link: CatalogLink) => {
	const [isCancel, setIsCancel] = useState(false);
	const isLoading = useCardLoading(link.name);
	const { isCloning, progress, error, start, clear } = useRemoteProgress(
		link.name,
		link.redirectOnClone,
		link.cloneCancelDisabled,
		setIsCancel,
	);
	const isCloneFinished = progress?.type === "finish";
	const isLinkCloning = isCloningStatus(link.status);

	useEffect(() => {
		if (isLinkCloning && !isCloning && !(isCloneFinished || progress?.type === "error")) start();
	}, [isLinkCloning, start, progress, isCloning, isCloneFinished]);

	// A finish kept past the clone would block the auto-start of the next clone on this card.
	useEffect(() => {
		if (isCloneFinished && !isLinkCloning) clear();
	}, [isCloneFinished, isLinkCloning, clear]);

	useEffect(() => {
		return () => {
			clearCardLoading(link.name);
		};
	}, [link.name]);

	const markOpening = useCallback(() => setCardLoading(link.name, true), [link.name]);

	const state = getCardState(link.status, { isCloning, isCloneFinished, error, isLoading });
	return { state, isCloning, progress, error, isCancel, setIsCancel, markOpening };
};

export default useCardState;
